import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import {
    MailWarning, RefreshCw, RotateCcw, Trash2, Send, Server, Cpu, Power, PauseCircle, Info, Inbox,
    Hourglass, Loader2, AlertOctagon, CheckCircle2,
} from 'lucide-react';
import CommonLoader from '../../components/CommonLoader';
import Pagination from '../../components/Pagination';
import {
    getEmailQueueStats, getEmailQueueJobs, retryEmailJob, removeEmailJob, retryAllFailedEmails, sendTestEmail,
} from '../../redux/emailQueueSlice';
import { formatTime } from '../../utils/clock';

// EMAIL QUEUE — the Super Admin's window on outbound mail. Every email the
// platform sends is queued on Redis (BullMQ) and delivered by a worker with
// retries; this page shows the queue's health, the jobs in each state, lets a
// failed mail be retried or dropped, and sends a test mail to prove the path.
// Without REDIS_URL the backend sends directly and there is no queue to show —
// the page says so instead of drawing an empty table. Refreshes itself every
// 15 s while open (silently — no loader flash) and on demand.

const MySwal = withReactContent(Swal);
const STATES = ['waiting', 'active', 'delayed', 'failed', 'completed'];
const STATE_ICONS = { waiting: Hourglass, active: Loader2, delayed: PauseCircle, failed: AlertOctagon, completed: CheckCircle2 };
const REFRESH_MS = 15000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fmtNum = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);
const fmtDateTime = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    // "15 Oct, 2:30 PM" — the time of day is 12-hour (utils/clock.js).
    return isNaN(d.getTime()) ? '—' : `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}, ${formatTime(d)}`;
};
const truncate = (s, n = 70) => {
    const str = String(s || '');
    return str.length > n ? `${str.slice(0, n)}…` : str;
};

const Badge = ({ tone = 'gray', icon: Icon, children, testid }) => {
    const tones = {
        green: 'bg-green-50 text-green-700 border-green-300',
        red: 'bg-red-50 text-red-700 border-red-300',
        amber: 'bg-amber-50 text-amber-700 border-amber-300',
        teal: 'bg-[#E7F5F4] text-[#2D8A8A] border-[#4ABDBD]/60',
        gray: 'bg-gray-100 text-gray-500 border-gray-300',
    };
    return (
        <span data-testid={testid} className={`inline-flex items-center gap-1.5 text-[11px] font-bold border rounded-full px-3 py-1 ${tones[tone] || tones.gray}`}>
            {Icon && <Icon className="w-3 h-3 flex-none" />}
            {children}
        </span>
    );
};

const EmailQueue = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { stats, statsLoading, jobs, jobsTotalItems, jobsLoading, mutatingId, testSending } = useSelector((state) => state.emailQueue);

    const [activeState, setActiveState] = useState('failed');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [testTo, setTestTo] = useState('');
    const [lastRefreshed, setLastRefreshed] = useState(null);
    // The interval reads these instead of closing over state, so it never
    // has to be re-created when a tab or page changes.
    const paramsRef = useRef({ state: activeState, page: currentPage, limit: itemsPerPage });
    const statsRef = useRef(stats);
    paramsRef.current = { state: activeState, page: currentPage, limit: itemsPerPage };
    statsRef.current = stats;

    const queueAvailable = (s) => Boolean(s?.enabled) && s?.redis !== 'not_configured';

    const refresh = async ({ silent = false } = {}) => {
        const statsResult = await dispatch(getEmailQueueStats({ silent }));
        if (getEmailQueueStats.rejected.match(statsResult)) {
            if (!silent) toast.error(t('emailQueue.stats_failed', 'Failed to load the email queue status.'));
            return;
        }
        const latest = statsResult.payload?.data;
        if (!queueAvailable(latest)) return;
        const jobsResult = await dispatch(getEmailQueueJobs({ ...paramsRef.current, silent }));
        if (getEmailQueueJobs.rejected.match(jobsResult)) {
            if (!silent) toast.error(t('emailQueue.jobs_failed', 'Failed to load the email queue jobs.'));
            return;
        }
        setLastRefreshed(new Date());
    };

    // First load + the silent 15 s tick, cleared on unmount.
    useEffect(() => {
        refresh();
        const id = setInterval(() => refresh({ silent: true }), REFRESH_MS);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadJobs = async (page, limit) => {
        if (!queueAvailable(statsRef.current)) return;
        const result = await dispatch(getEmailQueueJobs({ state: activeState, page, limit }));
        if (getEmailQueueJobs.rejected.match(result)) {
            toast.error(t('emailQueue.jobs_failed', 'Failed to load the email queue jobs.'));
        }
    };

    const selectState = (state) => {
        if (state === activeState) return;
        setActiveState(state);
        setCurrentPage(1);
        paramsRef.current = { state, page: 1, limit: itemsPerPage };
        if (!queueAvailable(statsRef.current)) return;
        dispatch(getEmailQueueJobs({ state, page: 1, limit: itemsPerPage })).then((result) => {
            if (getEmailQueueJobs.rejected.match(result)) toast.error(t('emailQueue.jobs_failed', 'Failed to load the email queue jobs.'));
        });
    };

    const handlePageChange = (page, newLimit) => {
        setCurrentPage(page);
        if (newLimit && newLimit !== itemsPerPage) setItemsPerPage(newLimit);
        loadJobs(page, newLimit || itemsPerPage);
    };

    const handleRetry = async (job) => {
        const result = await dispatch(retryEmailJob(job.id));
        if (retryEmailJob.fulfilled.match(result)) {
            toast.success(t('emailQueue.retry_queued', 'Email queued for another attempt.'));
            refresh({ silent: true });
        } else {
            toast.error(result.payload?.error || t('emailQueue.retry_failed', 'Failed to retry the email.'));
        }
    };

    const handleRemove = async (job) => {
        const confirmed = await MySwal.fire({
            title: t('emailQueue.remove_confirm_title', 'Remove this email from the queue?'),
            text: t('emailQueue.remove_confirm_text', 'It will not be sent. This cannot be undone.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('emailQueue.remove', 'Remove'),
            cancelButtonText: t('emailQueue.cancel', 'Cancel'),
        });
        if (!confirmed.isConfirmed) return;
        const result = await dispatch(removeEmailJob(job.id));
        if (removeEmailJob.fulfilled.match(result)) {
            toast.success(t('emailQueue.removed', 'Email removed from the queue.'));
            dispatch(getEmailQueueStats({ silent: true }));
        } else {
            toast.error(result.payload?.error || t('emailQueue.remove_failed', 'Failed to remove the email.'));
        }
    };

    const handleRetryAll = async () => {
        const failedCount = Number(stats?.counts?.failed) || 0;
        const confirmed = await MySwal.fire({
            title: t('emailQueue.retry_all_confirm_title', 'Retry every failed email?'),
            text: t('emailQueue.retry_all_confirm_text', '{{count}} failed email(s) will be queued again.', { count: failedCount }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('emailQueue.retry_all', 'Retry all failed'),
            cancelButtonText: t('emailQueue.cancel', 'Cancel'),
        });
        if (!confirmed.isConfirmed) return;
        const result = await dispatch(retryAllFailedEmails());
        if (retryAllFailedEmails.fulfilled.match(result)) {
            toast.success(t('emailQueue.retry_all_done', '{{count}} email(s) queued again.', { count: result.payload?.data?.retried ?? 0 }));
            refresh({ silent: true });
        } else {
            toast.error(result.payload?.error || t('emailQueue.retry_all_failed', 'Failed to retry the failed emails.'));
        }
    };

    const handleSendTest = async (e) => {
        e.preventDefault();
        const to = testTo.trim();
        if (!EMAIL_RE.test(to)) {
            toast.warning(t('emailQueue.test_invalid_email', 'Enter a valid email address.'));
            return;
        }
        const result = await dispatch(sendTestEmail({ to }));
        if (sendTestEmail.fulfilled.match(result)) {
            const mode = result.payload?.data?.mode;
            toast.success(mode === 'direct'
                ? t('emailQueue.test_sent_direct', 'Test email sent directly (no queue).')
                : t('emailQueue.test_sent_queued', 'Test email queued for delivery.'));
            setTestTo('');
            refresh({ silent: true });
        } else {
            toast.error(result.payload?.error || t('emailQueue.test_failed', 'Failed to send the test email.'));
        }
    };

    const redisTone = { up: 'green', down: 'red', not_configured: 'amber' }[stats?.redis] || 'gray';
    const redisLabel = {
        up: t('emailQueue.redis_up', 'Redis up'),
        down: t('emailQueue.redis_down', 'Redis down'),
        not_configured: t('emailQueue.redis_not_configured', 'Redis not configured'),
    }[stats?.redis] || t('emailQueue.redis_unknown', 'Redis unknown');
    const workerLabel = {
        inprocess: t('emailQueue.worker_inprocess', 'Worker: in-process'),
        external: t('emailQueue.worker_external', 'Worker: external'),
        off: t('emailQueue.worker_off', 'Worker: off'),
    }[stats?.worker] || t('emailQueue.worker_unknown', 'Worker: unknown');
    const workerTone = stats?.worker === 'off' ? 'red' : (stats?.worker ? 'teal' : 'gray');

    const stateLabel = (s) => t(`emailQueue.state_${s}`, s.charAt(0).toUpperCase() + s.slice(1));
    const showQueue = queueAvailable(stats);
    const failedCount = Number(stats?.counts?.failed) || 0;
    const showFirstLoader = statsLoading && !stats;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white w-full rounded-2xl p-4 sm:p-6 shadow-md"
            data-testid="email-queue-container"
        >
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4" data-testid="email-queue-header">
                <div>
                    <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent flex items-center gap-3" data-testid="email-queue-title">
                        <MailWarning className="w-7 h-7 text-[#4ABDBD]" />
                        {t('emailQueue.title', 'Email Queue')}
                    </h3>
                    <p className="mt-1 text-xs text-gray-400" data-testid="email-queue-subtitle">
                        {t('emailQueue.subtitle', 'Outbound mail — queue health, failed deliveries and retries')}
                        {lastRefreshed && (
                            <span className="ml-2">· {t('emailQueue.refreshed_at', 'Refreshed')} {formatTime(lastRefreshed)} · {t('emailQueue.auto_refresh', 'auto every 15 s')}</span>
                        )}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {showQueue && failedCount > 0 && (
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={handleRetryAll}
                            disabled={mutatingId === 'all'}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#D7A05C] to-[#B8823F] rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                            data-testid="email-queue-retry-all-button"
                        >
                            <RotateCcw className={`w-4 h-4 ${mutatingId === 'all' ? 'animate-spin' : ''}`} />
                            {t('emailQueue.retry_all', 'Retry all failed')} ({fmtNum(failedCount)})
                        </motion.button>
                    )}
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => refresh()}
                        disabled={statsLoading || jobsLoading}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                        data-testid="email-queue-refresh-button"
                    >
                        <RefreshCw className={`w-4 h-4 ${statsLoading || jobsLoading ? 'animate-spin' : ''}`} />
                        {t('emailQueue.refresh', 'Refresh')}
                    </motion.button>
                </div>
            </div>

            {showFirstLoader && (
                <div className="flex flex-col items-center justify-center py-4 pt-12" data-testid="email-queue-loading-container">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]">{t('reportsCommon.loading', 'Loading...')}</span>
                </div>
            )}

            {!showFirstLoader && stats && (
                <>
                    {/* Status strip */}
                    <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="email-queue-status-strip">
                        <Badge tone={stats.enabled ? 'green' : 'gray'} icon={Power} testid="email-queue-status-enabled">
                            {stats.enabled ? t('emailQueue.queue_enabled', 'Queue enabled') : t('emailQueue.queue_disabled', 'Queue disabled')}
                        </Badge>
                        <Badge tone={redisTone} icon={Server} testid="email-queue-status-redis">{redisLabel}</Badge>
                        <Badge tone={workerTone} icon={Cpu} testid="email-queue-status-worker">{workerLabel}</Badge>
                        {stats.paused && (
                            <Badge tone="amber" icon={PauseCircle} testid="email-queue-status-paused">{t('emailQueue.paused', 'Paused')}</Badge>
                        )}
                        {stats.config && (
                            <span className="text-[11px] text-gray-500 sm:ml-auto" data-testid="email-queue-config">
                                {t('emailQueue.config_attempts', 'Attempts')}: <b>{stats.config.attempts ?? '—'}</b>
                                {' · '}{t('emailQueue.config_backoff', 'Backoff')}: <b>{stats.config.backoffMs ?? '—'} ms</b>
                                {' · '}{t('emailQueue.config_concurrency', 'Concurrency')}: <b>{stats.config.concurrency ?? '—'}</b>
                                {' · '}{t('emailQueue.config_rate', 'Rate')}: <b>{stats.config.ratePerMinute ?? '—'}/min</b>
                            </span>
                        )}
                    </div>

                    {!showQueue && (
                        <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="email-queue-not-configured-notice">
                            <Info className="w-5 h-5 text-amber-600 flex-none mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold text-amber-800">
                                    {stats.redis === 'not_configured'
                                        ? t('emailQueue.not_configured_title', 'Emails are sent directly — set REDIS_URL to enable the queue')
                                        : t('emailQueue.queue_off_title', 'The email queue is switched off — emails are sent directly')}
                                </p>
                                <p className="text-xs text-amber-700 mt-0.5">
                                    {t('emailQueue.not_configured_text', 'Without a queue there are no retries and nothing to list here. The test email below still goes out through the direct path.')}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Count tiles = state tabs */}
                    {showQueue && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5" data-testid="email-queue-tiles">
                            {STATES.map((s) => {
                                const Icon = STATE_ICONS[s];
                                const active = activeState === s;
                                const n = Number(stats.counts?.[s]) || 0;
                                const alertTone = s === 'failed' && n > 0;
                                return (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => selectState(s)}
                                        aria-pressed={active}
                                        className={`relative rounded-2xl border p-4 text-left transition-all cursor-pointer ${active
                                            ? 'bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] text-white border-transparent shadow-lg'
                                            : `bg-white text-gray-700 hover:border-[#4ABDBD] hover:shadow-md ${alertTone ? 'border-red-200' : 'border-gray-200'}`}`}
                                        data-testid={`email-queue-tile-${s}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className={`text-[11px] font-semibold uppercase tracking-wider ${active ? 'text-white/80' : 'text-gray-500'}`}>{stateLabel(s)}</span>
                                            <Icon className={`w-4 h-4 ${active ? 'text-white/80' : (alertTone ? 'text-red-500' : 'text-[#4ABDBD]')} ${s === 'active' && n > 0 ? 'animate-spin' : ''}`} />
                                        </div>
                                        <p className={`mt-1 text-2xl font-bold tabular-nums ${!active && alertTone ? 'text-red-600' : ''}`} data-testid={`email-queue-tile-${s}-count`}>{fmtNum(n)}</p>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Jobs table */}
                    {showQueue && (
                        <div className="mb-6" data-testid="email-queue-jobs-section">
                            {jobsLoading ? (
                                <div className="flex flex-col items-center justify-center py-10" data-testid="email-queue-jobs-loading">
                                    <CommonLoader />
                                    <span className="text-xl text-[#4ABDBD]">{t('reportsCommon.loading', 'Loading...')}</span>
                                </div>
                            ) : (
                                <div className="overflow-x-auto rounded-md border border-gray-200" data-testid="email-queue-table-container">
                                    <table className="w-full border-collapse rounded-md overflow-hidden" data-testid="email-queue-table">
                                        <thead>
                                            <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">{t('emailQueue.col_to', 'To')}</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">{t('emailQueue.col_subject', 'Subject')}</th>
                                                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider">{t('emailQueue.col_attempts', 'Attempts')}</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">{t('emailQueue.col_reason', 'Failed reason')}</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap">{t('emailQueue.col_queued_at', 'Queued at')}</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap">
                                                    {activeState === 'completed' || activeState === 'failed' ? t('emailQueue.col_finished_at', 'Finished at') : t('emailQueue.col_next_retry', 'Next retry')}
                                                </th>
                                                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider">{t('emailQueue.col_actions', 'Actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {jobs.length === 0 ? (
                                                <tr data-testid="email-queue-empty-row">
                                                    <td colSpan="7" className="p-8 text-center text-gray-500">
                                                        <Inbox className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                                                        {t('emailQueue.no_jobs', 'No {{state}} emails.', { state: stateLabel(activeState).toLowerCase() })}
                                                    </td>
                                                </tr>
                                            ) : jobs.map((job, index) => {
                                                const busy = mutatingId !== null && String(mutatingId) === String(job.id);
                                                const canRetry = job.state === 'failed';
                                                const canRemove = ['failed', 'waiting', 'delayed'].includes(job.state);
                                                return (
                                                    <tr key={job.id || index} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300" data-testid={`email-queue-row-${index}`}>
                                                        <td className="px-4 py-4 text-xs text-gray-800 font-medium max-w-[220px] truncate" title={job.to}>{job.to || '—'}</td>
                                                        <td className="px-4 py-4 text-xs text-gray-700 max-w-[260px] truncate" title={job.subject}>{job.subject || '—'}</td>
                                                        <td className="px-4 py-4 text-xs text-center tabular-nums">
                                                            <span className={(job.attemptsMade || 0) >= (job.maxAttempts || 0) && job.state === 'failed' ? 'font-semibold text-red-600' : 'text-gray-700'}>
                                                                {job.attemptsMade ?? 0} / {job.maxAttempts ?? '—'}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-4 text-xs text-red-600 max-w-[300px]" title={job.failedReason || ''}>
                                                            {job.failedReason ? truncate(job.failedReason) : <span className="text-gray-300">—</span>}
                                                        </td>
                                                        <td className="px-4 py-4 text-xs text-gray-600 whitespace-nowrap">{fmtDateTime(job.queuedAt)}</td>
                                                        <td className="px-4 py-4 text-xs text-gray-600 whitespace-nowrap">
                                                            {activeState === 'completed' || activeState === 'failed' ? fmtDateTime(job.finishedOn) : fmtDateTime(job.nextRetryAt)}
                                                        </td>
                                                        <td className="px-4 py-4 text-center whitespace-nowrap">
                                                            <div className="inline-flex items-center gap-1.5">
                                                                {canRetry && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRetry(job)}
                                                                        disabled={busy}
                                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-lg text-[#2D8A8A] bg-[#E7F5F4] hover:bg-[#4ABDBD] hover:text-white transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                                        data-testid={`email-queue-retry-${index}`}
                                                                    >
                                                                        <RotateCcw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />
                                                                        {t('emailQueue.retry', 'Retry')}
                                                                    </button>
                                                                )}
                                                                {canRemove && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemove(job)}
                                                                        disabled={busy}
                                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-lg text-red-600 bg-red-50 hover:bg-red-600 hover:text-white transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                                        data-testid={`email-queue-remove-${index}`}
                                                                    >
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                        {t('emailQueue.remove', 'Remove')}
                                                                    </button>
                                                                )}
                                                                {!canRetry && !canRemove && <span className="text-xs text-gray-300">—</span>}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {!jobsLoading && jobsTotalItems > 0 && (
                                <div data-testid="email-queue-pagination-container">
                                    <Pagination
                                        totalItems={jobsTotalItems}
                                        currentPage={currentPage}
                                        itemsPerPage={itemsPerPage}
                                        onPageChange={handlePageChange}
                                        custombutton={jobs.length === 0 ? 'disable' : ''}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Send test email */}
                    <form onSubmit={handleSendTest} className="rounded-2xl border border-gray-200 bg-gray-50/60 p-4" data-testid="email-queue-test-form">
                        <h2 className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest text-[#2D8A8A] mb-2">
                            <Send className="w-3.5 h-3.5" />
                            {t('emailQueue.test_title', 'Send a test email')}
                        </h2>
                        <p className="text-xs text-gray-500 mb-3">{t('emailQueue.test_hint', 'Proves the delivery path end to end: the mail is queued (or sent directly when no queue is configured) and lands in the inbox you name.')}</p>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <input
                                type="email"
                                value={testTo}
                                onChange={(e) => setTestTo(e.target.value)}
                                placeholder={t('emailQueue.test_placeholder', 'name@example.com')}
                                className="w-full sm:max-w-sm px-3 py-2.5 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#4ABDBD] focus:ring-1 focus:ring-[#4ABDBD]/30 placeholder-gray-400 transition-all"
                                data-testid="email-queue-test-input"
                            />
                            <button
                                type="submit"
                                disabled={testSending || !testTo.trim()}
                                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                data-testid="email-queue-test-button"
                            >
                                <Send className="w-4 h-4" />
                                {testSending ? t('emailQueue.test_sending', 'Sending...') : t('emailQueue.test_send', 'Send test email')}
                            </button>
                        </div>
                    </form>
                </>
            )}
        </motion.div>
    );
};

export default EmailQueue;
